<template>
	<div class="form-group c-input-color">
		<label v-if="label" class="form__label" :for="slug">{{ label }}</label>

		<div class="color-picker">
			<!-- the visible swatch -->
			<div
				class="color-swatch"
				:style="swatchStyle"
				@click="openColorPicker"
			></div>

			<!-- hidden native color input -->
			<input
				ref="colorInput"
				:id="slug"
				type="color"
				:value="hexColor"
				@input="onColorInput"
				class="color-input"
			/>

			<!-- deselect button -->
			<button
				v-if="value !== null"
				type="button"
				class="btn-deselect"
				@click="deselectColor"
			>
				×
			</button>
		</div>

		<InputError v-if="hasError" :message="errorMessage" />
		<InputDescription v-if="description" :message="description" />
	</div>
</template>

<script>
import InputError from './input-error';
import InputDescription from './input-description';
import slugify from '@swipekit/lib/slugify.js';

export default {
	name: 'InputColor',

	components: { InputError, InputDescription },

	props: {
		label: { type: String, default: '' },
		value: { type: [Number, null], default: null }, // hue or null
		noStyle: { type: Boolean, default: false },
		description: { type: String, default: '' },
		hasError: { type: Boolean, default: false },
		errorMessage: { default: '' },
	},

	computed: {
		slug() {
			return slugify(this.label || `input_${Math.floor(Math.random() * 100)}`);
		},

		// how the swatch should look
		swatchStyle() {
			if (this.value === null) {
				return {
					backgroundColor: '#000',
				};
			}
			return {
				backgroundColor: `hsl(${this.value},100%,58%)`,
			};
		},

		// what color the hidden <input type="color"> should show when opened
		hexColor() {
			if (this.value === null) {
				// pick a default—browser will ignore it until user chooses
				return '#ffffff';
			}
			return this.hslToHex(this.value, 100, 50);
		},
	},

	methods: {
		openColorPicker() {
			this.$refs.colorInput.click();
		},

		onColorInput(e) {
			const pickedHex = e.target.value;
			const hue = this.hexToHue(pickedHex);
			this.$emit('input', hue);
		},

		deselectColor() {
			this.$emit('input', null);
		},

		// helper: HSL → HEX
		hslToHex(h, s, l) {
			s /= 100;
			l /= 100;
			const c = (1 - Math.abs(2 * l - 1)) * s;
			const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
			const m = l - c / 2;
			let [r, g, b] = [0, 0, 0];

			if (h < 60) [r, g, b] = [c, x, 0];
			else if (h < 120) [r, g, b] = [x, c, 0];
			else if (h < 180) [r, g, b] = [0, c, x];
			else if (h < 240) [r, g, b] = [0, x, c];
			else if (h < 300) [r, g, b] = [x, 0, c];
			else [r, g, b] = [c, 0, x];

			r = Math.round((r + m) * 255);
			g = Math.round((g + m) * 255);
			b = Math.round((b + m) * 255);

			return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
		},

		// helper: HEX → hue (0–360)
		hexToHue(hex) {
			const i = parseInt(hex.slice(1), 16);
			let r = ((i >> 16) & 0xff) / 255;
			let g = ((i >> 8) & 0xff) / 255;
			let b = (i & 0xff) / 255;

			const max = Math.max(r, g, b),
				min = Math.min(r, g, b);
			const d = max - min;
			let h = 0;

			if (d) {
				if (max === r) h = 60 * (((g - b) / d) % 6);
				else if (max === g) h = 60 * ((b - r) / d + 2);
				else h = 60 * ((r - g) / d + 4);
			}

			if (h < 0) h += 360;
			return Math.round(h);
		},
	},
};
</script>

<style lang="scss">
.c-input-color {
	display: flex;
	flex-direction: column;
	padding-bottom: var(--margin-lg);

	.color-picker {
		display: flex;
		align-items: center;
	}

	.color-input {
		position: absolute;
		top: 0;
		left: 0;
		width: 32px;
		height: 32px;
		padding: 0;
		margin: 0;
		border: none;
		opacity: 0;
		cursor: pointer;
	}

	.color-swatch {
		width: 32px;
		height: 32px;
		border-radius: var(--border-radius);
		border: #fff solid 3px;
		box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
		cursor: pointer;
		margin-right: 8px;
	}

	.btn-deselect {
		width: 32px;
		height: 32px;
		border-radius: var(--border-radius);
		border: #fff solid 3px;
		background: var(--color-bg-3);
		border: none;
		font-size: 1.25em;
		line-height: 1;
		cursor: pointer;
		padding: 0 4px;
	}
}
</style>
